// API-Helfer des Pakets „Extras" – dünne, typisierte Hüllen um `api()` aus lib/api (Vertrag: docs/API.md).
// Nur Routen, die die Extras-Oberflächen brauchen; die Shell hat ihre eigenen Helfer.
import type {
  AdminStatsDto,
  AdminUserDto,
  ApiScope,
  ApiTokenCreatedDto,
  ApiTokenDto,
  AuditLogEntryDto,
  BookmarkDto,
  BookmarkFolderDto,
  BotDto,
  BridgeChannelDto,
  BridgeChannelMode,
  BridgeGuildDto,
  BridgeStatusDto,
  ChannelDto,
  CursorPage,
  ExportFormat,
  ExportJobDto,
  OutgoingWebhookDeliveryDto,
  OutgoingWebhookDto,
  OutgoingWebhookEvent,
  PlatformLimits,
  ReminderDto,
  ScheduledMessageDto,
  ScheduledStatus,
  SearchResultDto,
  ServerDto,
  SoundDto,
  UserDto,
  WebhookDto,
} from '@lagerfeuer/shared';
import { DEFAULT_LIMITS } from '@lagerfeuer/shared';
import { useQuery } from '@tanstack/react-query';
import { api } from '../../lib/api';

type QueryValue = string | number | boolean | null | undefined | readonly (string | number)[];

/**
 * Query-Parameter für `api(path, { query })` aufbereiten: leere Werte weglassen, Arrays als wiederholte Schlüssel.
 * Der Web-Core-Client (`lib/api` → `assertSafePath`) lehnt `?` im Pfad ab – Query daher nie in den Pfad schreiben.
 */
export function toQuery(params: Record<string, QueryValue>): Record<string, string | string[]> {
  const out: Record<string, string | string[]> = {};
  for (const [key, value] of Object.entries(params)) {
    if (value === undefined || value === null || value === '') continue;
    out[key] = Array.isArray(value) ? (value as readonly (string | number)[]).map(String) : String(value);
  }
  return out;
}

/** Query-String bauen (für Links/Anzeige): leere Werte weglassen, Arrays als wiederholte Schlüssel (`?has=a&has=b`). */
export function qs(params: Record<string, QueryValue>): string {
  const sp = new URLSearchParams();
  for (const [key, value] of Object.entries(toQuery(params))) {
    if (Array.isArray(value)) for (const v of value) sp.append(key, v);
    else sp.set(key, value);
  }
  const s = sp.toString();
  return s ? `?${s}` : '';
}

const enc = encodeURIComponent;

function upload(file: File): FormData {
  const fd = new FormData();
  fd.append('file', file);
  return fd;
}

// ---------- Admin (1.4, 1.5, 4.7) ----------

export type AdminUserFilter = 'admins' | 'disabled' | 'bots' | 'shadow';
export type AdminServer = ServerDto & { owner: UserDto };
export interface AdminStorageDto {
  totalBytes: number;
  byPrefix: Record<string, { bytes: number; count: number }>;
  topUsers: { user: UserDto; bytes: number }[];
}
export interface AdminLogLine {
  ts: string;
  level: string;
  context: string;
  message: string;
}
export interface PasswordResetResult {
  resetToken?: string;
  resetUrl?: string;
  expiresAt?: string;
}
export interface AvailableGuild {
  discordGuildId: string;
  name: string;
  iconUrl: string | null;
  memberCount: number;
  mirrored: boolean;
}

export const adminApi = {
  stats: () => api<AdminStatsDto>('/admin/stats'),
  storage: () => api<AdminStorageDto>('/admin/storage'),
  logs: (p: { lines?: number; level?: string } = {}) => api<{ lines: AdminLogLine[] }>('/admin/logs', { query: toQuery({ ...p }) }),

  users: (p: { q?: string; after?: string; limit?: number; filter?: AdminUserFilter } = {}) =>
    api<CursorPage<AdminUserDto>>('/admin/users', { query: toQuery({ ...p }) }),
  user: (id: string) => api<AdminUserDto>(`/admin/users/${enc(id)}`),
  createUser: (body: { username: string; password: string; email?: string; isAdmin?: boolean }) =>
    api<AdminUserDto>('/admin/users', { method: 'POST', json: body }),
  updateUser: (
    id: string,
    body: Partial<{ username: string; displayName: string | null; email: string | null; isAdmin: boolean; disabled: boolean; disabledReason: string | null }>,
  ) => api<AdminUserDto>(`/admin/users/${enc(id)}`, { method: 'PATCH', json: body }),
  resetPassword: (id: string, body: { mode: 'token' } | { mode: 'password'; newPassword: string }) =>
    api<PasswordResetResult>(`/admin/users/${enc(id)}/password-reset`, { method: 'POST', json: body }),
  logoutUser: (id: string) => api<void>(`/admin/users/${enc(id)}/logout`, { method: 'POST' }),
  deleteUser: (id: string) => api<void>(`/admin/users/${enc(id)}`, { method: 'DELETE' }),

  servers: (p: { q?: string; after?: string; limit?: number; mirrored?: boolean } = {}) =>
    api<CursorPage<AdminServer>>('/admin/servers', { query: toQuery({ ...p }) }),
  updateServer: (id: string, body: { ownerId?: string; name?: string }) =>
    api<ServerDto>(`/admin/servers/${enc(id)}`, { method: 'PATCH', json: body }),
  deleteServer: (id: string) => api<void>(`/admin/servers/${enc(id)}`, { method: 'DELETE' }),

  limits: () => api<PlatformLimits>('/admin/limits'),
  updateLimits: (body: Partial<PlatformLimits>) => api<PlatformLimits>('/admin/limits', { method: 'PATCH', json: body }),

  auditLogs: (p: { before?: string; limit?: number; action?: string; actorId?: string; serverId?: string } = {}) =>
    api<AuditLogEntryDto[]>('/admin/audit-logs', { query: toQuery({ ...p }) }),

  bridge: () => api<BridgeStatusDto>('/admin/bridge'),
  availableGuilds: () => api<AvailableGuild[]>('/admin/bridge/available-guilds'),
  importGuild: (body: { discordGuildId: string; historyLimit?: number; defaultMode?: BridgeChannelMode }) =>
    api<BridgeGuildDto>('/admin/bridge/guilds', { method: 'POST', json: body }),
  updateGuild: (id: string, body: { status?: 'PAUSED' | 'ACTIVE'; historyLimit?: number; defaultMode?: BridgeChannelMode }) =>
    api<BridgeGuildDto>(`/admin/bridge/guilds/${enc(id)}`, { method: 'PATCH', json: body }),
  resyncGuild: (id: string) => api<void>(`/admin/bridge/guilds/${enc(id)}/resync`, { method: 'POST' }),
  removeGuild: (id: string, deleteServer: boolean) =>
    api<void>(`/admin/bridge/guilds/${enc(id)}`, { method: 'DELETE', query: toQuery({ deleteServer: deleteServer || undefined }) }),
  setChannelMode: (id: string, mode: BridgeChannelMode) =>
    api<BridgeChannelDto>(`/admin/bridge/channels/${enc(id)}`, { method: 'PATCH', json: { mode } }),
};

// ---------- Öffentliche Konfiguration (1.1 `GET /auth/config`) ----------

/** Antwort von `GET /auth/config` (docs/API.md 1.1). */
export interface AuthConfigDto {
  registrationOpen: boolean;
  discordLogin: boolean;
  vapidPublicKey: string | null;
  maxUploadBytes: number;
  maxMessageLength: number;
}

export const AUTH_CONFIG_KEY = ['extras', 'auth-config'] as const;

export const configApi = {
  get: () => api<AuthConfigDto>('/auth/config'),
};

/** Gecachte Plattform-Konfiguration (ändert sich selten; 5 min frisch). */
export function useAuthConfig() {
  return useQuery({ queryKey: AUTH_CONFIG_KEY, queryFn: configApi.get, staleTime: 5 * 60_000, gcTime: 30 * 60_000 });
}

/** Maximale Nachrichtenlänge laut Server; solange lädt oder bei Fehler `DEFAULT_LIMITS.maxMessageLength`. */
export function useMaxMessageLength(): number {
  const v = useAuthConfig().data?.maxMessageLength;
  return typeof v === 'number' && v > 0 ? v : DEFAULT_LIMITS.maxMessageLength;
}

// ---------- Allgemein: Server, Kanäle, Nutzer (für Auswahllisten) ----------

export const lookupApi = {
  servers: () => api<ServerDto[]>('/servers'),
  channels: (serverId: string) => api<ChannelDto[]>(`/servers/${enc(serverId)}/channels`),
  channel: (channelId: string) => api<ChannelDto>(`/channels/${enc(channelId)}`),
  searchUsers: (q: string, limit = 10) => api<UserDto[]>('/users/search', { query: toQuery({ q, limit }) }),
};

// ---------- Suche (3.8) ----------

export const SEARCH_HAS_TYPES = ['link', 'file', 'image', 'video', 'embed', 'sticker', 'sound', 'poll'] as const;
export type SearchHasType = (typeof SEARCH_HAS_TYPES)[number];
export type SearchSort = 'relevance' | 'newest' | 'oldest';

export interface SearchParams {
  q?: string;
  serverId?: string;
  channelId?: string[];
  authorId?: string[];
  mentions?: string;
  has?: SearchHasType[];
  pinned?: boolean;
  before?: string;
  after?: string;
  sort?: SearchSort;
  offset?: number;
  limit?: number;
}

export const searchApi = {
  messages: (p: SearchParams) => api<SearchResultDto>('/search/messages', { query: toQuery({ ...p }) }),
};

// ---------- Geplante Nachrichten (3.9) ----------

export const scheduledApi = {
  list: (p: { channelId?: string; status?: ScheduledStatus } = {}) => api<ScheduledMessageDto[]>('/scheduled-messages', { query: toQuery({ ...p }) }),
  create: (channelId: string, body: { content: string; sendAt: string; attachmentIds?: string[]; replyToId?: string; flags?: number }) =>
    api<ScheduledMessageDto>(`/channels/${enc(channelId)}/scheduled-messages`, { method: 'POST', json: body }),
  update: (id: string, body: { content?: string; sendAt?: string; attachmentIds?: string[] }) =>
    api<ScheduledMessageDto>(`/scheduled-messages/${enc(id)}`, { method: 'PATCH', json: body }),
  cancel: (id: string) => api<void>(`/scheduled-messages/${enc(id)}`, { method: 'DELETE' }),
};

// ---------- Erinnerungen (3.10) ----------

export const remindersApi = {
  list: (p: { done?: boolean } = {}) => api<ReminderDto[]>('/reminders', { query: toQuery({ ...p }) }),
  create: (body: { remindAt: string; note?: string; messageId?: string }) => api<ReminderDto>('/reminders', { method: 'POST', json: body }),
  update: (id: string, body: { remindAt?: string; note?: string | null; done?: boolean }) =>
    api<ReminderDto>(`/reminders/${enc(id)}`, { method: 'PATCH', json: body }),
  remove: (id: string) => api<void>(`/reminders/${enc(id)}`, { method: 'DELETE' }),
};

// ---------- Lesezeichen (3.11) ----------

export const bookmarksApi = {
  folders: () => api<BookmarkFolderDto[]>('/bookmarks/folders'),
  createFolder: (body: { name: string; color?: string | null }) => api<BookmarkFolderDto>('/bookmarks/folders', { method: 'POST', json: body }),
  updateFolder: (id: string, body: { name?: string; color?: string | null; position?: number }) =>
    api<BookmarkFolderDto>(`/bookmarks/folders/${enc(id)}`, { method: 'PATCH', json: body }),
  deleteFolder: (id: string) => api<void>(`/bookmarks/folders/${enc(id)}`, { method: 'DELETE' }),
  list: (p: { folderId?: string; before?: string; limit?: number } = {}) => api<BookmarkDto[]>('/bookmarks', { query: toQuery({ ...p }) }),
  create: (body: { messageId: string; folderId?: string; note?: string }) => api<BookmarkDto>('/bookmarks', { method: 'POST', json: body }),
  update: (id: string, body: { folderId?: string | null; note?: string | null }) =>
    api<BookmarkDto>(`/bookmarks/${enc(id)}`, { method: 'PATCH', json: body }),
  remove: (id: string) => api<void>(`/bookmarks/${enc(id)}`, { method: 'DELETE' }),
};

// ---------- Export (3.12) ----------

export const exportsApi = {
  create: (channelId: string, body: { format: ExportFormat; after?: string; before?: string; includeAttachments?: boolean }) =>
    api<ExportJobDto>(`/channels/${enc(channelId)}/exports`, { method: 'POST', json: body }),
  list: () => api<ExportJobDto[]>('/exports'),
  get: (id: string) => api<ExportJobDto>(`/exports/${enc(id)}`),
  remove: (id: string) => api<void>(`/exports/${enc(id)}`, { method: 'DELETE' }),
};

// ---------- Bots & API-Tokens (1.6) ----------

export const botsApi = {
  list: () => api<BotDto[]>('/bots'),
  create: (body: { username: string; displayName?: string }) =>
    api<{ bot: BotDto; token: ApiTokenCreatedDto }>('/bots', { method: 'POST', json: body }),
  update: (id: string, body: { username?: string; displayName?: string | null; bio?: string | null }) =>
    api<BotDto>(`/bots/${enc(id)}`, { method: 'PATCH', json: body }),
  setAvatar: (id: string, file: File) => api<BotDto>(`/bots/${enc(id)}/avatar`, { method: 'PUT', body: upload(file) }),
  deleteAvatar: (id: string) => api<BotDto>(`/bots/${enc(id)}/avatar`, { method: 'DELETE' }),
  remove: (id: string) => api<void>(`/bots/${enc(id)}`, { method: 'DELETE' }),
  addToServer: (id: string, serverId: string) => api<void>(`/bots/${enc(id)}/servers/${enc(serverId)}`, { method: 'PUT' }),
};

export const tokensApi = {
  list: (ownerId?: string) => api<ApiTokenDto[]>('/tokens', { query: toQuery({ ownerId }) }),
  create: (body: { name: string; scopes: ApiScope[]; expiresAt?: string; ownerId?: string }) =>
    api<ApiTokenCreatedDto>('/tokens', { method: 'POST', json: body }),
  revoke: (id: string) => api<void>(`/tokens/${enc(id)}`, { method: 'DELETE' }),
};

// ---------- Webhooks (3.13) ----------

export const webhooksApi = {
  forServer: (serverId: string) => api<WebhookDto[]>(`/servers/${enc(serverId)}/webhooks`),
  forChannel: (channelId: string) => api<WebhookDto[]>(`/channels/${enc(channelId)}/webhooks`),
  create: (channelId: string, body: { name: string }) =>
    api<WebhookDto>(`/channels/${enc(channelId)}/webhooks`, { method: 'POST', json: body }),
  update: (id: string, body: { name?: string; channelId?: string }) =>
    api<WebhookDto>(`/webhooks/${enc(id)}`, { method: 'PATCH', json: body }),
  setAvatar: (id: string, file: File) => api<WebhookDto>(`/webhooks/${enc(id)}/avatar`, { method: 'PUT', body: upload(file) }),
  deleteAvatar: (id: string) => api<WebhookDto>(`/webhooks/${enc(id)}/avatar`, { method: 'DELETE' }),
  regenerateToken: (id: string) => api<WebhookDto>(`/webhooks/${enc(id)}/token`, { method: 'POST' }),
  remove: (id: string) => api<void>(`/webhooks/${enc(id)}`, { method: 'DELETE' }),
};

export const outgoingWebhooksApi = {
  list: (serverId: string) => api<OutgoingWebhookDto[]>(`/servers/${enc(serverId)}/outgoing-webhooks`),
  create: (serverId: string, body: { name: string; url: string; events: OutgoingWebhookEvent[]; channelId?: string }) =>
    api<OutgoingWebhookDto>(`/servers/${enc(serverId)}/outgoing-webhooks`, { method: 'POST', json: body }),
  update: (id: string, body: { name?: string; url?: string; events?: OutgoingWebhookEvent[]; channelId?: string | null; active?: boolean }) =>
    api<OutgoingWebhookDto>(`/outgoing-webhooks/${enc(id)}`, { method: 'PATCH', json: body }),
  regenerateSecret: (id: string) => api<OutgoingWebhookDto>(`/outgoing-webhooks/${enc(id)}/secret`, { method: 'POST' }),
  test: (id: string) => api<OutgoingWebhookDeliveryDto>(`/outgoing-webhooks/${enc(id)}/test`, { method: 'POST' }),
  deliveries: (id: string, p: { before?: string; limit?: number } = {}) =>
    api<OutgoingWebhookDeliveryDto[]>(`/outgoing-webhooks/${enc(id)}/deliveries`, { query: toQuery({ ...p }) }),
  remove: (id: string) => api<void>(`/outgoing-webhooks/${enc(id)}`, { method: 'DELETE' }),
};

// ---------- Soundboard (2.6) ----------

export const soundsApi = {
  list: (serverId: string) => api<SoundDto[]>(`/servers/${enc(serverId)}/sounds`),
  play: (channelId: string, soundId: string) =>
    api<void>(`/channels/${enc(channelId)}/soundboard`, { method: 'POST', json: { soundId } }),
};

/** Ziel-URL für „Zur Nachricht springen" (Shell-Route, siehe web-core: `?message=<id>`). */
export function messageLink(serverId: string | null | undefined, channelId: string, messageId?: string | null): string {
  const base = `/channels/${serverId ?? '@me'}/${channelId}`;
  return messageId ? `${base}?message=${enc(messageId)}` : base;
}
